import { Utilities } from '@approot/utilities';
import {
  isDropdownOptionValid,
  makeTextCustomColor,
  optionsChangeHandler
} from './leg.utilities';
import { AbstractControl, FormGroup } from '@angular/forms';
import {
  CutOffTimes,
  FixingSource,
  Leg,
  ProductOrderData
} from '@approot/models';
import { PercentPipe } from '@angular/common';
import {
  firstDateToUseFn,
  lastDateToUseFn,
  numberOfXToUseFn
} from '@approot/utilities/schedule-utilities';
import {
  getAttributeControlFromKey,
  getAttributeFromKey
} from './leg-attribute-utilities';
import { ACCUMULATOR_MULTI_PERIOD } from '@approot/constants';

export function setPercentageOnLoad(
  keyName: string,
  form: FormGroup,
  leg: Leg,
  percentPipe: PercentPipe
) {
  // loads the a value of percentage format into the form
  const percentageValue = leg.preSetPosition[keyName];
  if (
    typeof percentageValue === 'string' && // if the value is copied then no conversion needs to be done
    percentageValue.substring(percentageValue.length - 1) === '%'
  ) {
    form.get(keyName).setValue(percentageValue);
  } else if (
    percentageValue !== '' &&
    percentageValue !== Utilities.customText
  ) {
    form
      .get(keyName)
      .setValue(percentPipe.transform(percentageValue, '1.0-12'));
  } else if (percentageValue === Utilities.customText) {
    form.get(keyName).setValue(percentageValue);
    makeTextCustomColor(keyName, Utilities.orangeTextColor, leg);
  }
}

export function dataToCopyModifier(
  product: Record<string, any>,
  form: FormGroup,
  leg: Leg,
  dataToCopy: Record<string, any>
) {
  const attributeNames = product[0].attributes.map(
    attribute => attribute.shortName
  );

  const firstDateUsed = firstDateToUseFn(form);
  const lastDateUsed = lastDateToUseFn(form);
  copyLegNbToUseSetter(form, attributeNames, dataToCopy);

  if (
    (firstDateUsed === 'firstSettlementDate' ||
      lastDateUsed === 'lastSettlementDate') &&
    dataToCopy['schedule'] !== undefined
  ) {
    dataToCopy['schedule'] = null;
  }

  copyLegFirstDateSetter(form, attributeNames, dataToCopy);
  copyLegLastDateSetter(form, attributeNames, dataToCopy);

  if (leg.productType === 'newdcd') {
    // wipe the margin attribute on if product type is newdcd
    dataToCopy.margin = '';
  }

  if (product[0].identifier === 'spot') {
    delete dataToCopy['settlementDate'];
  }

  delete dataToCopy['tradeId'];
  delete dataToCopy['timestamp'];

  // Copy any currency-like values without extra text
  Object.entries(dataToCopy).map(([name, value]: [string, string]) => {
    if (/^([\d\,\.]+) [A-Z]{3}( |$)/.test(value)) {
      dataToCopy[name] = RegExp.$1.replace(/\,\./g, '');
    }
  });
}

export function setupFirstOfEnumDefault(
  leg: Leg,
  attributeDefaults: Record<string, string>[],
  form: FormGroup,
  attributesDisplayList: ProductOrderData[],
  useDefaults: boolean
) {
  let defaults = [];
  if (!leg.newProduct && leg.preSetPosition) {
    defaults = attributeDefaults;
  }

  // For each drop down, where a preSetPosition doesn't exist we make the first of the list be the default
  Object.keys(form.value).forEach(attributeKey => {
    const dropdownAttribute = getAttributeFromKey(leg, attributeKey);

    const allowSetting =
      dropdownAttribute?.type === 'enum' &&
      form.get(attributeKey) &&
      (!leg.preSetPosition[attributeKey] ||
        !isDropdownOptionValid(
          attributeKey,
          leg.preSetPosition[attributeKey],
          leg
        )) && // if the dropdown doesn't have a presetvalue or the value is not a valid option of the dropdown
      ((!dropdownAttribute?.optional && !leg.newProduct) || leg.newProduct); // only default it if its required

    if (!allowSetting) {
      return;
    }

    switch (attributeKey) {
      case 'cross':
      case 'secondCross':
      case 'direction':
      case 'fixingSource':
      case 'cutOff':
      case 'secondDirection':
      case 'counterPartyA':
      case 'counterPartyB':
        break;
      case 'lowerBarrierType':
      case 'lowerBarrierObservationType':
      case 'upperBarrierType':
      // @ts-ignore
      case 'upperBarrierObservationType':
        if (
          leg.productType === 'accumulatorspotsettled' ||
          leg.productType === 'accumulator' ||
          leg.productType === ACCUMULATOR_MULTI_PERIOD
        ) {
          // for accumulators the barriers depend on the direction set
          // we don't want to set the direction so don't touch these
          break;
        }
      // tslint:disable: no-switch-case-fall-through
      default:
        // before setting the dropdown to the first of the option, if its an old leg attempt to use defaults
        if (defaults?.length) {
          const longName = attributesDisplayList.find(
            y => y.shortName === attributeKey
          )?.longName; // get the long of the attribute as this is how its stored in the application

          if (longName) {
            /* some fields e.g. position1 doesn't have a display list name to link with,
            as they are part of a group of fields on a single line */
            const defaultData = defaults.find(def => def.key === longName); // get the index
            if (
              defaultData && // if there is a default for the attribute and the value is valid, then use this value
              isDropdownOptionValid(attributeKey, defaultData.value, leg)
            ) {
              form.get(attributeKey).setValue(defaultData.value);
              break;
            }
          }
        }

        if (
          dropdownAttribute.value &&
          dropdownAttribute.value.length &&
          leg.newProduct &&
          useDefaults
        ) {
          // if there is a value, use the first option in the array
          form.get(attributeKey).setValue(dropdownAttribute.value[0]);
        }
        break;
    }
  });
}

export function setPresetCutOff(
  presetValue: string,
  keyFormControl: AbstractControl,
  cutOffs: CutOffTimes[],
  isLegCopied: boolean
): void {
  let cutOff = cutOffs.find(
    x => x.code === presetValue || x.longName === presetValue
  );

  if (!cutOff) {
    return;
  }
  keyFormControl.setValue(cutOff.longName, { emitEvent: !isLegCopied });
}

export function setPresetFixingSource(
  presetValue: string,
  keyFormControl: AbstractControl,
  fixingSources: FixingSource[],
  isLegCopied: boolean
): void {
  let fixingSource = fixingSources.find(
    x => x.code === presetValue || x.longName === presetValue
  );

  if (!fixingSource) {
    return;
  }
  keyFormControl.setValue(fixingSource.longName, { emitEvent: !isLegCopied });
}

function copyLegNbToUseSetter(
  form: FormGroup,
  attributeNames: string,
  dataToCopy: Record<string, any>
): void {
  const numberOfXUsed = numberOfXToUseFn(form);

  if (numberOfXUsed) {
    // attempt to set the number of x
    // nbFixings <==> nbExpiries <===> nbSettlementDates
    let nbToUse = '';
    if (attributeNames.includes('nbFixings')) {
      nbToUse = 'nbFixings';
    } else if (attributeNames.includes('nbExpiries')) {
      nbToUse = 'nbExpiries';
    } else if (attributeNames.includes('nbSettlementDates')) {
      nbToUse = 'nbSettlementDates';
    } else if (attributeNames.includes('nbExerciseDates')) {
      nbToUse = 'nbExerciseDates';
    }

    if (nbToUse && nbToUse !== numberOfXUsed) {
      dataToCopy[nbToUse] = dataToCopy[numberOfXUsed];
      delete dataToCopy[numberOfXUsed];
    }
  }
}

function copyLegLastDateSetter(
  form: FormGroup,
  attributeNames: string,
  dataToCopy: Record<string, any>
): void {
  const lastDateUsed = lastDateToUseFn(form);
  if (lastDateUsed && lastDateUsed !== 'lastSettlementDate') {
    // attempt to set last dates
    // lastFixingDate <==> lastExpirationDate
    let lastDateToUse;
    if (attributeNames.includes('lastFixingDate')) {
      lastDateToUse = 'lastFixingDate';
    } else if (attributeNames.includes('lastExpirationDate')) {
      lastDateToUse = 'lastExpirationDate';
    } else if (attributeNames.includes('lastSettlementDate')) {
      lastDateToUse = 'lastSettlementDate';
      // we don't want to transfer schedules that have a expiry/fixing to settlements, so wipe it
      if (dataToCopy['schedule'] !== undefined) {
        dataToCopy['schedule'] = null;
      }
    }
    if (
      lastDateToUse &&
      lastDateUsed !== lastDateToUse &&
      lastDateToUse !== 'lastSettlementDate'
    ) {
      dataToCopy[lastDateToUse] = dataToCopy[lastDateUsed];
      delete dataToCopy[lastDateUsed];
    }
  }
}

function copyLegFirstDateSetter(
  form: FormGroup,
  attributeNames: string,
  dataToCopy: Record<string, any>
): void {
  const firstDateUsed = firstDateToUseFn(form);
  if (firstDateUsed && firstDateUsed !== 'firstSettlementDate') {
    // attempt to set first dates
    // firstFixingDate <==> firstExpirationDate
    let firstDateToUse;
    if (attributeNames.includes('firstFixingDate')) {
      firstDateToUse = 'firstFixingDate';
    } else if (attributeNames.includes('firstExpirationDate')) {
      firstDateToUse = 'firstExpirationDate';
    } else if (attributeNames.includes('firstSettlementDate')) {
      firstDateToUse = 'firstSettlementDate';
      // we don't want to transfer schedules that have a expiry/fixing to settlements, so wipe it
      if (dataToCopy['schedule'] !== undefined) {
        dataToCopy['schedule'] = null;
      }
    } else if (attributeNames.includes('firstExerciseDate')) {
      firstDateToUse = 'firstExerciseDate';
    }

    if (
      firstDateToUse &&
      firstDateUsed !== firstDateToUse &&
      firstDateToUse !== 'firstSettlementDate'
    ) {
      dataToCopy[firstDateToUse] = dataToCopy[firstDateUsed];
      delete dataToCopy[firstDateUsed];
    }
  }
}

export function postLoadingPresetsTpf(form: FormGroup, leg: Leg): void {
  if (
    leg.productType !== 'choosertpf' &&
    leg.productType !== 'frozenchoosertpf' &&
    leg.productType !== 'chooserpivottpf' &&
    form.get('targetProfitAdjustmentType') &&
    isDropdownOptionValid(
      'targetProfitAdjustmentType',
      leg.preSetPosition['targetProfitAdjustmentType'],
      leg
    )
  ) {
    form
      .get('targetProfitAdjustmentType')
      .setValue(leg.preSetPosition['targetProfitAdjustmentType']);
  }

  if (
    leg.productType !== 'choosertpf' &&
    leg.productType !== 'frozenchoosertpf' &&
    leg.productType !== 'chooserpivottpf' &&
    form.get('targetProfitUnit') &&
    isDropdownOptionValid(
      'targetProfitUnit',
      leg.preSetPosition['targetProfitUnit'],
      leg
    )
  ) {
    if (form.get('targetProfitUnit')) {
      form
        .get('targetProfitUnit')
        .setValue(leg.preSetPosition['targetProfitUnit']);
    }
    if (form.get('targetProfitAdjustmentType')) {
      form
        .get('targetProfitAdjustmentType')
        .setValue(leg.preSetPosition['targetProfitAdjustmentType']);
    }
  }
}

export function postLoadingPresetsDcd(
  form: FormGroup,
  leg: Leg,
  tradingMode: boolean
): void {
  if (
    form.get('startDate') &&
    form.get('startDate').value &&
    form.get('redemptionDate') &&
    form.get('expiryDate')
  ) {
    // if start date enable the redemptionDate and expiryDate
    form.get('redemptionDate').enable({ emitEvent: false });
    form.get('expiryDate').enable({ emitEvent: false });
  }

  if (leg.productType === 'newdcd' && tradingMode) {
    // we need to set default dcd related variable values
    if (!form.get('conversionRate').value) {
      leg.missingAttributeOnPricing = 'conversionRate';
      leg.lastEditedDcdAttribute = 'depositRate';
    } else if (!form.get('depositRate').value) {
      leg.missingAttributeOnPricing = 'depositRate';
      leg.lastEditedDcdAttribute = 'margin';
    } else if (!form.get('margin').value) {
      leg.missingAttributeOnPricing = 'margin';
      leg.lastEditedDcdAttribute = 'depositRate';
    }
  }
}

export function setupLegs(form: FormGroup, leg: Leg): void {
  basketSetup(form, leg);
  accumulatorsSetup(form, leg);
  setUpCustomFormFunctionality(form);
}

function basketSetup(form: FormGroup, leg: Leg): void {
  let basketStrike = form.get('basketStrike');
  if (basketStrike) {
    let basketStrikeVal = getAttributeControlFromKey(
      'basketStrike',
      leg.attributesAsFormControls
    ).validator;

    if (!basketStrikeVal.includes('negativeInteger')) {
      basketStrikeVal.push('negativeInteger');
      basketStrikeVal.shift();
    }
  }
}

function accumulatorsSetup(form: FormGroup, leg: Leg): void {
  if (
    !leg.fromTab &&
    (leg.productType === 'accumulator' ||
      leg.productType === 'accumulatorspotsettled')
  ) {
    optionsChangeHandler(form, leg, 'upperBarrierType', [], ''); // wipe all by default
    optionsChangeHandler(form, leg, 'lowerBarrierType', [], '');
    optionsChangeHandler(form, leg, 'upperBarrierObservationType', [], '');
    optionsChangeHandler(form, leg, 'lowerBarrierObservationType', [], '');
  }
}

function setUpCustomFormFunctionality(form: FormGroup): void {
  // if we have these 3 input fields, then we'll set up strike * notional multiplier
  form.get('expiryDate')?.disable();
  form.get('settlementDate')?.disable();
  if (form.get('cross')) {
    form.get('windowStartDate')?.disable();
    form.get('windowEndDate')?.disable();
    form.get('windowDate')?.disable();
  }
}
